import type { RouteObject } from "react-router-dom";
import { Navigate, useLocation } from "react-router-dom";
import AdminLayout from "@/components/layout/AdminLayout";
import PortalLayout from "@/components/layout/PortalLayout";
import DriverLayout from "@/components/layout/DriverLayout";
import NotFound from "@/pages/NotFound";
import RequireAuth, { RedirectIfAuthenticated } from "@/auth/RequireAuth";
import { lazyRoute } from "./lazyRoute";

/**
 * 라우트 테이블.
 * 레이아웃과 NotFound 만 즉시 로드하고 화면은 전부 lazy 로 분할한다
 * (`lazyRoute` — 배포로 청크가 갈렸을 때 한 번 자동 새로고침하는 래퍼).
 * 경로 규칙은 프로토타입(readdy)의 구조를 승계하되,
 * - 하행 가실적: `downward-estimate` → `downbound-estimate`
 * - 기준정보: `client` 단일 화면 → `company / location / item / vehicle / price / plan`
 * 로 요구사항에 맞춰 정리했다.
 */

// 배차관리 (REQ-D)
const DispatchOverviewPage = lazyRoute(() => import("@/pages/admin/dispatch/page"));
const DispatchOrdersPage = lazyRoute(() => import("@/pages/admin/dispatch/orders/page"));
const DispatchEditPage = lazyRoute(() => import("@/pages/admin/dispatch/[id]/edit/page"));
const DispatchOperationPage = lazyRoute(
  () => import("@/pages/admin/dispatch/[id]/operation/page")
);

// 일일실적 (REQ-P)
const PerformancePage = lazyRoute(() => import("@/pages/admin/performance/page"));
const MesUploadPage = lazyRoute(() => import("@/pages/admin/performance/mes-upload/page"));
const MonthlyPerformancePage = lazyRoute(
  () => import("@/pages/admin/performance/monthly/page")
);
const ClosingUploadPage = lazyRoute(
  () => import("@/pages/admin/performance/closing-upload/page")
);
const DownboundEstimatePage = lazyRoute(
  () => import("@/pages/admin/performance/downbound-estimate/page")
);

// 송장내역확인 (REQ-I)
const InvoiceListPage = lazyRoute(() => import("@/pages/admin/invoice/page"));
const InvoiceDetailPage = lazyRoute(() => import("@/pages/admin/invoice/[id]/page"));
const InvoiceUnmatchedPage = lazyRoute(() => import("@/pages/admin/invoice/unmatched/page"));

// 결산업무 (REQ-S)
const SettlementPage = lazyRoute(() => import("@/pages/admin/settlement/page"));
const SettlementSummaryPage = lazyRoute(() => import("@/pages/admin/settlement/summary/page"));
const StatisticsPage = lazyRoute(() => import("@/pages/admin/statistics/page"));

// 기준정보 (REQ-M)
const CompanyMasterPage = lazyRoute(() => import("@/pages/admin/master/company/page"));
const LocationMasterPage = lazyRoute(() => import("@/pages/admin/master/location/page"));
const ItemMasterPage = lazyRoute(() => import("@/pages/admin/master/item/page"));
const RouteMasterPage = lazyRoute(() => import("@/pages/admin/master/route/page"));
const VehicleMasterPage = lazyRoute(() => import("@/pages/admin/master/vehicle/page"));
const PriceMasterPage = lazyRoute(() => import("@/pages/admin/master/price/page"));
const PlanMasterPage = lazyRoute(() => import("@/pages/admin/master/plan/page"));
const CompanyProfilePage = lazyRoute(() => import("@/pages/admin/master/profile/page"));

// 기사 모바일 웹 (REQ-A)
const DriverHomePage = lazyRoute(() => import("@/pages/driver/home/page"));
const DriverInvoiceListPage = lazyRoute(() => import("@/pages/driver/invoice/page"));
const DriverInvoiceSubmitPage = lazyRoute(() => import("@/pages/driver/invoice/submit/page"));
const DriverPerformancePage = lazyRoute(() => import("@/pages/driver/performance/page"));
const DriverProfilePage = lazyRoute(() => import("@/pages/driver/profile/page"));
const DriverNotificationsPage = lazyRoute(() => import("@/pages/driver/notifications/page"));

// 인증
const LoginPage = lazyRoute(() => import("@/pages/login/page"));
const DriverLoginPage = lazyRoute(() => import("@/pages/driver/login/page"));

// 업체 포털 — 계약 §4.1 "하도급업체 관리자" 채널
const CarrierLoginPage = lazyRoute(() => import("@/pages/carrier/login/page"));
const CarrierOrdersPage = lazyRoute(() => import("@/pages/carrier/orders/page"));
const CarrierInvoicePage = lazyRoute(() => import("@/pages/carrier/invoice/page"));
const CarrierProfilePage = lazyRoute(() => import("@/pages/carrier/profile/page"));

// 청구업체 포털 — 매출처가 자기 청구분의 운송장 사진을 본다 (고객 요청 2026-09-23)
const ClientLoginPage = lazyRoute(() => import("@/pages/client/login/page"));
const ClientInvoicesPage = lazyRoute(() => import("@/pages/client/invoices/page"));
const ClientProfilePage = lazyRoute(() => import("@/pages/client/profile/page"));

// 기사관리 (REQ-A 담당자 쪽 — 기사 제출을 받아 처리한다)
const DisputePage = lazyRoute(() => import("@/pages/admin/drivers/disputes/page"));
const VehicleChangePage = lazyRoute(() => import("@/pages/admin/drivers/vehicle-changes/page"));
const NoticePage = lazyRoute(() => import("@/pages/admin/drivers/notices/page"));
const DriverAccountPage = lazyRoute(() => import("@/pages/admin/drivers/accounts/page"));

// 사용자 관리 (시스템관리자 전용)
const UserManagementPage = lazyRoute(() => import("@/pages/admin/users/page"));

// 변경 이력 (시스템관리자 전용) — 계정을 지울 때 "변경 이력에 남는다" 고 적어 온 것을
// 확인하는 자리다. 사용자 관리 바로 아래 메뉴라 읽은 자리에서 한 칸이다.
const AuditLogPage = lazyRoute(() => import("@/pages/admin/audit-logs/page"));

// 운영·진단 (시스템관리자 전용) — **사이드바에 메뉴가 없다.** 주소를 아는 사람만 들어온다.
// 운송·정산 업무가 아니라 시스템 운영 수치라, 담당자가 매일 보는 메뉴에 끼우지 않았다.
const OcrCostPage = lazyRoute(() => import("@/pages/admin/ops/ocr-cost/page"));
const ProdCopyPage = lazyRoute(() => import("@/pages/admin/ops/prod-copy/page"));

/**
 * 권한 구획
 * - `/admin/*` 는 담당자 이상(담당자·시스템관리자)
 * - `/admin/users` 와 `/admin/ops/*` 는 시스템관리자만 (요구사항 4.1)
 * - `/driver/*` 는 기사
 * - `/carrier/*` 는 업체, `/client/*` 는 청구업체 (각자 소속 범위만 본다)
 * - 로그인 화면만 공개. 이미 인증됐으면 자기 역할의 홈으로 돌려보낸다
 */
/**
 * `배차등록`·`업체 배차` 가 `배차 등록` 화면의 두 탭으로 합쳐지기 전 주소.
 *
 * **쿼리를 그대로 옮긴다.** MES 미매칭에서 만드는 링크가 운송일자·차량·상행 도착지·업로드 ID 를
 * 싣고 오는데, 그걸 흘리면 담당자는 빈 폼을 마주하고 원장은 미매칭으로 남는다.
 */
function LegacyDispatchRedirect({ tab }: { tab: "drivers" | "carriers" }) {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  params.set("tab", tab);
  // `/dispatch/new` 는 등록 화면 그 자체였다. 목록만 띄우면 누른 사람이 헛걸음한다.
  if (tab === "drivers") params.set("new", "1");
  return <Navigate to={`/admin/dispatch/orders?${params}`} replace />;
}

const ADMIN_ROLES = ["담당자", "시스템관리자"] as const;
const SYSTEM_ADMIN_ROLES = ["시스템관리자"] as const;
const DRIVER_ROLES = ["기사"] as const;
const CARRIER_ROLES = ["업체"] as const;
const CLIENT_ROLES = ["청구업체"] as const;

const routes: RouteObject[] = [
  { path: "/", element: <Navigate to="/admin/dispatch" replace /> },

  // 로그인 화면은 **그 화면의 역할로 이미 들어와 있을 때만** 홈으로 튕긴다.
  // 담당자로 로그인한 채 기사 로그인을 열 수 있어야 기사앱을 시험해 볼 수 있다.
  {
    path: "/login",
    element: (
      <RedirectIfAuthenticated roles={ADMIN_ROLES}>
        <LoginPage />
      </RedirectIfAuthenticated>
    ),
  },
  {
    path: "/driver/login",
    element: (
      <RedirectIfAuthenticated roles={DRIVER_ROLES}>
        <DriverLoginPage />
      </RedirectIfAuthenticated>
    ),
  },

  {
    path: "/admin",
    element: (
      <RequireAuth roles={ADMIN_ROLES}>
        <AdminLayout />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <Navigate to="/admin/dispatch" replace /> },

      { path: "dispatch", element: <DispatchOverviewPage /> },
      { path: "dispatch/orders", element: <DispatchOrdersPage /> },
      // 옛 경로는 **쿼리를 지닌 채** 새 탭으로 보낸다 (2026-09-09 화면 통합).
      // MES 미매칭에서 만든 링크와 도움말·QA 문서·북마크가 아직 이쪽을 가리킨다.
      { path: "dispatch/new", element: <LegacyDispatchRedirect tab="drivers" /> },
      { path: "dispatch/carrier-orders", element: <LegacyDispatchRedirect tab="carriers" /> },
      { path: "dispatch/:id/edit", element: <DispatchEditPage /> },
      { path: "dispatch/:id/operation", element: <DispatchOperationPage /> },

      { path: "performance", element: <PerformancePage /> },
      { path: "performance/mes-upload", element: <MesUploadPage /> },
      { path: "performance/monthly", element: <MonthlyPerformancePage /> },
      { path: "performance/closing-upload", element: <ClosingUploadPage /> },
      { path: "performance/downbound-estimate", element: <DownboundEstimatePage /> },

      { path: "invoice", element: <InvoiceListPage /> },
      { path: "invoice/unmatched", element: <InvoiceUnmatchedPage /> },
      { path: "invoice/:id", element: <InvoiceDetailPage /> },

      { path: "settlement", element: <SettlementPage /> },
      { path: "settlement/summary", element: <SettlementSummaryPage /> },
      { path: "statistics", element: <StatisticsPage /> },

      { path: "master/company", element: <CompanyMasterPage /> },
      { path: "master/location", element: <LocationMasterPage /> },
      { path: "master/item", element: <ItemMasterPage /> },
      { path: "master/route", element: <RouteMasterPage /> },
      { path: "master/vehicle", element: <VehicleMasterPage /> },
      { path: "master/price", element: <PriceMasterPage /> },
      { path: "master/plan", element: <PlanMasterPage /> },
      { path: "master/profile", element: <CompanyProfilePage /> },

      { path: "drivers/disputes", element: <DisputePage /> },
      { path: "drivers/vehicle-changes", element: <VehicleChangePage /> },
      { path: "drivers/notices", element: <NoticePage /> },
      { path: "drivers/accounts", element: <DriverAccountPage /> },

      {
        path: "users",
        element: (
          <RequireAuth roles={SYSTEM_ADMIN_ROLES}>
            <UserManagementPage />
          </RequireAuth>
        ),
      },
      {
        path: "audit-logs",
        element: (
          <RequireAuth roles={SYSTEM_ADMIN_ROLES}>
            <AuditLogPage />
          </RequireAuth>
        ),
      },

      // 사이드바에 없는 화면. 라우트 가드는 그대로 걸어 둔다 —
      // "주소를 모르면 못 들어온다" 는 권한이 아니다.
      {
        path: "ops/ocr-cost",
        element: (
          <RequireAuth roles={SYSTEM_ADMIN_ROLES}>
            <OcrCostPage />
          </RequireAuth>
        ),
      },
      {
        path: "ops/prod-copy",
        element: (
          <RequireAuth roles={SYSTEM_ADMIN_ROLES}>
            <ProdCopyPage />
          </RequireAuth>
        ),
      },
    ],
  },

  {
    path: "/carrier/login",
    element: (
      <RedirectIfAuthenticated roles={CARRIER_ROLES}>
        <CarrierLoginPage />
      </RedirectIfAuthenticated>
    ),
  },

  {
    path: "/carrier",
    element: (
      <RequireAuth roles={CARRIER_ROLES}>
        <PortalLayout />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <Navigate to="/carrier/orders" replace /> },
      { path: "orders", element: <CarrierOrdersPage /> },
      { path: "invoice", element: <CarrierInvoicePage /> },
      { path: "profile", element: <CarrierProfilePage /> },
    ],
  },

  {
    path: "/client/login",
    element: (
      <RedirectIfAuthenticated roles={CLIENT_ROLES}>
        <ClientLoginPage />
      </RedirectIfAuthenticated>
    ),
  },

  {
    path: "/client",
    element: (
      <RequireAuth roles={CLIENT_ROLES}>
        <PortalLayout />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <Navigate to="/client/invoices" replace /> },
      { path: "invoices", element: <ClientInvoicesPage /> },
      { path: "profile", element: <ClientProfilePage /> },
    ],
  },

  {
    path: "/driver",
    element: (
      <RequireAuth roles={DRIVER_ROLES}>
        <DriverLayout />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <Navigate to="/driver/home" replace /> },
      { path: "home", element: <DriverHomePage /> },
      { path: "invoice", element: <DriverInvoiceListPage /> },
      { path: "invoice/submit", element: <DriverInvoiceSubmitPage /> },
      { path: "performance", element: <DriverPerformancePage /> },
      { path: "notifications", element: <DriverNotificationsPage /> },
      { path: "profile", element: <DriverProfilePage /> },
    ],
  },

  { path: "*", element: <NotFound /> },
];

export default routes;
